import React, { ReactNode, useEffect, useState, useLayoutEffect } from "react";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";

interface CanvasWrapperProps {
  width: number;
  setWidth: React.Dispatch<React.SetStateAction<number>>;
  image: string;
  children: ReactNode;
}

const useStyles = makeStyles(
  () => ({
    scaledContent: {
      width: "100%",
      transformOrigin: "top left",
      transition: "transform 0.3s ease",
    },
  }),
  { index: 1 },
);

const CanvasWrapper: React.FC<CanvasWrapperProps> = ({ width, setWidth, image, children }) => {
  const [scaleFactor, setScaleFactor] = useState<number>(0.6);
  const [originalHeight, setOriginalHeight] = useState<number>(1); // Add state for original height
  const classes = useStyles();

  useEffect(() => {
    const img = new Image();
    img.src = image;

    img.onload = () => {
      setWidth(img.width);
    };

    return () => {
      img.onload = null;
    };
  }, [image, setWidth]);

  useLayoutEffect(() => {
    const updateScaleFactor = () => {
      const contentElement = document.getElementById("canvas-wrapper");

      if (contentElement && width) {
        const newScaleFactor = contentElement.offsetWidth / contentElement.scrollWidth;
        const newOriginalHeight = contentElement.scrollHeight; // Get the total height of the content, including any overflow
        setScaleFactor(newScaleFactor);
        setOriginalHeight(newOriginalHeight);
      }
    };

    // Update scale factor when the window is resized
    window.addEventListener("resize", updateScaleFactor);
    updateScaleFactor(); // Initial calculation

    return () => {
      // Cleanup event listener on component unmount
      window.removeEventListener("resize", updateScaleFactor);
    };
  }, [width]);

  const scaledHeight = originalHeight * scaleFactor; // Calculate the scaled height

  return (
    <Box id="canvas-wrapper" className={classes.scaledContent} sx={{ transform: `scale(${scaleFactor})`, height: `${scaledHeight}px` }}>
      {children}
    </Box>
  );
};

export default CanvasWrapper;
